import React, { useState } from 'react';
import {
  Users,
  MessageSquare,
  AlertTriangle,
  TrendingUp,
  Award,
  Brain,
  Network,
  Clock,
  ArrowRight,
  BarChart3
} from 'lucide-react';
import {
  AgentCollaboration,
  AgentCommunication
} from '../../../types/aiAgent';
import { 
  ConflictResolution,
  CrossAgentLearning,
  PerformanceBenchmark
} from '../../../services/agentCollaborationService';
import { clickable } from "components/ui/interactive";

interface AgentCollaborationDashboardProps {
  collaborations: AgentCollaboration[];
  communications: AgentCommunication[];
  conflicts: ConflictResolution[];
  learningTransfers: CrossAgentLearning[];
  benchmarks: PerformanceBenchmark[];
  onCreateCollaboration: (request: any) => void;
  onResolveConflict: (conflictId: string) => void;
  onShareKnowledge: (knowledge: any) => void;
}

export const AgentCollaborationDashboard: React.FC<AgentCollaborationDashboardProps> = ({
  collaborations,
  communications,
  conflicts,
  learningTransfers,
  benchmarks,
  onCreateCollaboration,
  onResolveConflict,
  onShareKnowledge
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'collaborations' | 'conflicts' | 'learning' | 'benchmarks'>('overview');
  const [selectedCollaboration, setSelectedCollaboration] = useState<string | null>(null);

  const activeCollaborations = (collaborations || []).filter(c => c.status === 'active');
  const pendingConflicts = (conflicts || []).filter(c => c.status === 'pending' || c.status === 'in_progress');
  const recentCommunications = (communications || []).slice(-10);

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-content flex items-center gap-2">
              <Network className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              Agent Collaboration Center
            </h2>
            <p className="text-sm text-content-secondary mt-1">
              Coordinate multi-agent testing, resolve conflicts, and share knowledge
            </p>
          </div>
          <button
            onClick={() => onCreateCollaboration?.({})}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            New Collaboration
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-300">Active Collaborations</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{activeCollaborations.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600 dark:text-blue-300" />
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-300">Pending Conflicts</p>
                <p className="text-2xl font-bold text-orange-900 dark:text-orange-300">{pendingConflicts.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600 dark:text-orange-300" />
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-300">Knowledge Transfers</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-300">{(learningTransfers || []).length}</p>
              </div>
              <Brain className="w-8 h-8 text-green-600 dark:text-green-300" />
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600 dark:text-purple-300">Communications</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">{(communications || []).length}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-600 dark:text-purple-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'collaborations', label: 'Collaborations', icon: Users },
            { id: 'conflicts', label: 'Conflicts', icon: AlertTriangle },
            { id: 'learning', label: 'Learning', icon: Brain },
            { id: 'benchmarks', label: 'Benchmarks', icon: Award }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <OverviewTab
            collaborations={collaborations}
            communications={recentCommunications}
            conflicts={pendingConflicts}
            learningTransfers={learningTransfers}
          />
        )}

        {activeTab === 'collaborations' && (
          <CollaborationsTab
            collaborations={collaborations}
            selectedCollaboration={selectedCollaboration}
            onSelectCollaboration={setSelectedCollaboration}
            onCreateCollaboration={onCreateCollaboration}
          />
        )}

        {activeTab === 'conflicts' && (
          <ConflictsTab
            conflicts={conflicts}
            onResolveConflict={onResolveConflict}
          />
        )}

        {activeTab === 'learning' && (
          <LearningTab
            learningTransfers={learningTransfers}
            onShareKnowledge={onShareKnowledge}
          />
        )}

        {activeTab === 'benchmarks' && (
          <BenchmarksTab
            benchmarks={benchmarks}
          />
        )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{
  collaborations: AgentCollaboration[];
  communications: AgentCommunication[];
  conflicts: ConflictResolution[];
  learningTransfers: CrossAgentLearning[];
}> = ({ collaborations, communications, conflicts, learningTransfers: _learningTransfers }) => {
  return (
    <div className="space-y-6">
      {/* Recent Activity */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <h3 className="font-medium text-content mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Recent Communications
          </h3>
          <div className="space-y-3">
            {(communications || []).slice(0, 5).map((comm) => (
              <div key={comm.id} className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-content truncate">
                    {comm.content.message}
                  </p>
                  <p className="text-xs text-content-muted">
                    {new Date(comm.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <h3 className="font-medium text-content mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Collaboration Status
          </h3>
          <div className="space-y-3">
            {(collaborations || []).slice(0, 5).map((collab) => (
              <div key={collab.id} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-content truncate">
                    {collab.objective}
                  </p>
                  <p className="text-xs text-content-muted">
                    {collab.collaboratorIds.length} agents
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  collab.status === 'active' ? 'bg-green-100 text-green-800' :
                  collab.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200'
                }`}>
                  {collab.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(conflicts || []).length > 0 && (
        <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-300" />
            <h3 className="font-medium text-orange-900 dark:text-orange-300">Attention Required</h3>
          </div>
          <p className="text-sm text-orange-800 dark:text-orange-300">
            {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} need{conflicts.length === 1 ? 's' : ''} resolution
          </p>
        </div>
      )}
    </div>
  );
};

// Collaborations Tab Component
const CollaborationsTab: React.FC<{
  collaborations: AgentCollaboration[];
  selectedCollaboration: string | null;
  onSelectCollaboration: (id: string | null) => void;
  onCreateCollaboration: (request: any) => void;
}> = ({ collaborations, selectedCollaboration: _selectedCollaboration, onSelectCollaboration, onCreateCollaboration }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-content">Active Collaborations</h3>
        <button
          onClick={() => onCreateCollaboration?.({})}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          Create New
        </button>
      </div>

      <div className="grid gap-4">
        {(collaborations || []).map((collaboration) => (
          <div
            key={collaboration.id}
            className="border border-line rounded-lg p-4 hover:border-line-strong transition-colors cursor-pointer"
            {...clickable(() => onSelectCollaboration(collaboration.id))}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-content">{collaboration.objective}</h4>
                <p className="text-sm text-content-secondary mt-1">
                  {collaboration.collaboratorIds.length} agents • {collaboration.type}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    collaboration.status === 'active' ? 'bg-green-100 text-green-800' :
                    collaboration.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {collaboration.status}
                  </span>
                  <span className="text-xs text-content-muted">
                    Started {new Date(collaboration.startedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-content-subtle" />
            </div>

            {collaboration.communications.length > 0 && (
              <div className="mt-3 pt-3 border-t border-line-subtle">
                <p className="text-xs text-content-muted">
                  Latest: {collaboration.communications[collaboration.communications.length - 1].content.message}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Conflicts Tab Component
const ConflictsTab: React.FC<{
  conflicts: ConflictResolution[];
  onResolveConflict: (conflictId: string) => void;
}> = ({ conflicts, onResolveConflict }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-content">Conflict Resolution</h3>

      <div className="grid gap-4">
        {(conflicts || []).map((conflict) => (
          <div
            key={conflict.id}
            className="border border-line rounded-lg p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`w-4 h-4 ${
                    conflict.status === 'pending' ? 'text-orange-500 dark:text-orange-400' :
                    conflict.status === 'resolved' ? 'text-green-500 dark:text-green-400' :
                    'text-red-500 dark:text-red-400'
                  }`} />
                  <h4 className="font-medium text-content">{conflict.conflictType}</h4>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    conflict.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                    conflict.status === 'resolved' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                    conflict.status === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                    'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {conflict.status}
                  </span>
                </div>
                <p className="text-sm text-content-secondary mb-2">{conflict.description}</p>
                <p className="text-xs text-content-muted">
                  Involves: {conflict.involvedAgents.join(', ')} • 
                  Strategy: {conflict.resolutionStrategy}
                </p>
              </div>
              {conflict.status === 'pending' && (
                <button
                  onClick={() => onResolveConflict(conflict.id)}
                  className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition-colors"
                >
                  Resolve
                </button>
              )}
            </div>

            {conflict.resolution && (
              <div className="mt-3 pt-3 border-t border-line-subtle">
                <p className="text-sm text-content font-medium">Resolution:</p>
                <p className="text-sm text-content-secondary">{conflict.resolution.reasoning}</p>
                <p className="text-xs text-content-muted mt-1">
                  Confidence: {Math.round(conflict.resolution.confidence * 100)}%
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Learning Tab Component
const LearningTab: React.FC<{
  learningTransfers: CrossAgentLearning[];
  onShareKnowledge: (knowledge: any) => void;
}> = ({ learningTransfers, onShareKnowledge }) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-content">Knowledge Sharing</h3>
        <button
          onClick={() => onShareKnowledge?.({})}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-2"
        >
          <Brain className="w-4 h-4" />
          Share Knowledge
        </button>
      </div>

      <div className="grid gap-4">
        {(learningTransfers || []).map((learning) => (
          <div
            key={learning.id}
            className="border border-line rounded-lg p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h4 className="font-medium text-content">{learning.knowledge.title}</h4>
                <p className="text-sm text-content-secondary mt-1">{learning.knowledge.description}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                learning.learningType === 'pattern' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                learning.learningType === 'strategy' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                learning.learningType === 'failure' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
              }`}>
                {learning.learningType}
              </span>
            </div>

            <div className="flex items-center gap-4 text-sm text-content-muted">
              <span>From: Agent {learning.sourceAgentId.slice(-8)}</span>
              <span>To: {learning.targetAgentIds.length} agents</span>
              <span>Effectiveness: {learning.knowledge.effectiveness}%</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(learning.adoptionStatus).map(([agentId, status]) => (
                <span
                  key={agentId}
                  className={`px-2 py-1 text-xs rounded-full ${
                    status === 'adopted' ? 'bg-green-100 text-green-800' :
                    status === 'adapted' ? 'bg-blue-100 text-blue-800' :
                    status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200'
                  }`}
                >
                  {agentId.slice(-8)}: {status}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Benchmarks Tab Component
const BenchmarksTab: React.FC<{
  benchmarks: PerformanceBenchmark[];
}> = ({ benchmarks }) => {
  const sortedBenchmarks = [...(benchmarks || [])].sort((a, b) => a.ranking.overall - b.ranking.overall);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-content">Performance Benchmarks</h3>

      <div className="grid gap-4">
        {sortedBenchmarks.map((benchmark, index) => (
          <div
            key={benchmark.agentId}
            className="border border-line rounded-lg p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  index === 0 ? 'bg-yellow-100 text-yellow-800' :
                  index === 1 ? 'bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200' :
                  index === 2 ? 'bg-orange-100 text-orange-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  #{benchmark.ranking.overall}
                </div>
                <div>
                  <h4 className="font-medium text-content">
                    Agent {benchmark.agentId.slice(-8)}
                  </h4>
                  <p className="text-sm text-content-secondary">{benchmark.specialization}</p>
                </div>
              </div>
              {index < 3 && (
                <Award className={`w-5 h-5 ${
                  index === 0 ? 'text-yellow-500 dark:text-yellow-400' :
                  index === 1 ? 'text-content-muted' :
                  'text-orange-500 dark:text-orange-400'
                }`} />
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-content">
                  {Math.round(benchmark.metrics.successRate * 100)}%
                </p>
                <p className="text-xs text-content-muted">Success Rate</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-content">
                  {Math.round(benchmark.metrics.averageConfidence * 100)}%
                </p>
                <p className="text-xs text-content-muted">Confidence</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-content">
                  {Math.round(benchmark.metrics.improvementRate * 100)}%
                </p>
                <p className="text-xs text-content-muted">Improvement</p>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <TrendingUp className={`w-4 h-4 ${
                benchmark.trends.improvement > 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'
              }`} />
              <span className={`text-sm ${
                benchmark.trends.improvement > 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'
              }`}>
                {benchmark.trends.improvement > 0 ? '+' : ''}{Math.round(benchmark.trends.improvement * 100)}% trend
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgentCollaborationDashboard;